<template>
  <div
    class="verdict-card"
    :class="{ 'verdict-card--generating': generating }"
    role="button"
    :tabindex="generating ? -1 : 0"
    :aria-label="computedAriaLabel"
    :aria-busy="generating ? 'true' : undefined"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Meta row: short ID + time -->
    <div class="verdict-card__meta">
      <span class="verdict-card__short-id font-mono">{{ debateShortId || '—' }}</span>
      <RelativeTime v-if="!generating" :timestamp="updatedAt" class="verdict-card__time" />
    </div>

    <!-- Protocol row -->
    <p class="verdict-card__protocol font-mono">{{ protocol }}</p>

    <!-- Subject row -->
    <h3 class="verdict-card__subject">
      <template v-if="generating">
        <span class="verdict-card__skeleton verdict-card__skeleton--subject" aria-hidden="true" />
      </template>
      <template v-else>{{ subject }}</template>
    </h3>

    <!-- Headline row -->
    <p class="verdict-card__headline">
      <template v-if="generating">
        <span class="verdict-card__skeleton verdict-card__skeleton--headline-1" aria-hidden="true" />
        <span class="verdict-card__skeleton verdict-card__skeleton--headline-2" aria-hidden="true" />
      </template>
      <template v-else>{{ headline }}</template>
    </p>

    <!-- Footer row -->
    <div class="verdict-card__footer">
      <!-- Avatar strip or skeleton -->
      <div class="verdict-card__avatars">
        <template v-if="generating">
          <span class="verdict-card__skeleton verdict-card__skeleton--avatars" aria-hidden="true" />
        </template>
        <template v-else>
          <KdsAvatarStack :segments="avatarSegments" :max="5" :size="20" />
        </template>
      </div>

      <!-- Counts + tone badge or generating badge -->
      <div class="verdict-card__footer-right">
        <KdsBadge :tone="generating ? 'muted' : consensusTone">
          {{ generating ? t('verdicts.generating') : toneLabel }}
        </KdsBadge>
        <template v-if="!generating">
          <span class="verdict-card__count font-mono">
            {{ t('verdicts.card.insightsCount', insightsCount, { n: insightsCount }) }}
          </span>
          <span class="verdict-card__count font-mono">
            {{ t('verdicts.card.frictionsCount', frictionsCount, { n: frictionsCount }) }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsAvatarStack from '@/kds/primitives/KdsAvatarStack.vue';
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import RelativeTime from '@/components/RelativeTime.vue';
import type { KdsBadgeTone, PersonaSegment } from '@/kds/types';

const props = withDefaults(
  defineProps<{
    id: string;
    debateShortId: string;
    subject: string;
    protocol: string;
    updatedAt: string;
    headline: string;
    consensusTone: KdsBadgeTone;
    consensusScore: number;
    insightsCount: number;
    frictionsCount: number;
    personaCount: number;
    avatarSegments: PersonaSegment[];
    generating?: boolean;
  }>(),
  { generating: false }
);

const { t } = useI18n();
const router = useRouter();

// Human-readable tone label for the badge via i18n (D2 — PO decision 2026-05-22).
const toneLabel = computed(() =>
  t(`verdicts.card.toneLabels.${props.consensusTone}`)
);

const computedAriaLabel = computed(() => {
  if (props.generating) {
    return t('verdicts.card.generatingAriaLabel', { subject: props.subject });
  }
  return t('verdicts.card.ariaLabel', {
    subject: props.subject,
    protocol: props.protocol,
    tone: toneLabel.value,
    timeAgo: props.updatedAt
  });
});

const handleClick = () => {
  if (props.generating) return;
  void router.push({ name: 'verdict', params: { id: props.id } });
};
</script>

<style scoped lang="scss">
.verdict-card {
  // Layout
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);

  // Box model
  border-radius: var(--r-4);
  border: var(--border);
  width: 100%;

  // Visuals
  background: var(--paper);
  box-shadow: var(--shadow-1);

  // Typography
  color: var(--ink);

  // Interaction
  cursor: pointer;
  user-select: none;

  // Transition
  transition:
    box-shadow var(--d-fast) var(--ease-out),
    border-color var(--d-fast) var(--ease-out);

  &:hover:not(.verdict-card--generating) {
    box-shadow: var(--shadow-2);
    border-color: var(--mist-2);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  // Generating state
  &--generating {
    opacity: 0.65;
    cursor: default;
    pointer-events: none;
  }

  // Meta row
  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  &__short-id {
    font-size: var(--fs-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  &__time {
    font-size: var(--fs-meta);
    color: var(--stone-2);
  }

  // Protocol row
  &__protocol {
    margin: 0;
    font-size: var(--fs-mono);
    color: var(--stone);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  // Subject row
  &__subject {
    margin: 0;
    font-family: var(--font-serif);
    font-size: var(--fs-h3);
    font-weight: var(--fw-regular);
    color: var(--ink);
    line-height: 1.32;

    // Truncate 2 lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Headline row
  &__headline {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--stone);
    line-height: 1.45;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Footer row
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-top: auto;
    padding-top: var(--space-2);
    border-top: var(--border);
  }

  &__avatars {
    display: flex;
    align-items: center;
  }

  &__footer-right {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__count {
    font-size: var(--fs-meta);
    color: var(--stone);
    white-space: nowrap;
  }

  // Skeleton placeholders for generating state
  &__skeleton {
    display: block;
    background: var(--paper-3);
    border-radius: var(--r-1);
    animation: skeleton-pulse 1.6s ease-in-out infinite;

    &--subject {
      height: 20px;
      width: 60%;
    }

    &--headline-1 {
      height: 14px;
      width: 100%;
      margin-bottom: var(--space-1);
    }

    &--headline-2 {
      height: 14px;
      width: 75%;
    }

    &--avatars {
      height: 20px;
      width: 80px;
      border-radius: var(--r-pill);
    }
  }
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@media (prefers-reduced-motion: reduce) {
  .verdict-card {
    transition: none;

    &__skeleton {
      animation: none;
    }
  }
}
</style>
